/**
 * StripeOverview.jsx – Phase 1a: KPI Dashboard
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, Users, CreditCard, AlertTriangle, Clock,
  CheckCircle2, XCircle, Loader2, RefreshCw, Euro,
  FileText, Ban, ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── MRR-Aufschlüsselung (anonymisiert) ──────────────────────────────────────
function MrrBreakdown({ breakdown, d }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-semibold">MRR-Aufschlüsselung ({breakdown.length} Abonnement{breakdown.length !== 1 ? 'e' : ''})</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 pb-4 pt-3 space-y-4">
          {/* Zusammenfassung */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-muted-foreground">Standort-MRR</p>
              <p className="text-sm font-bold text-blue-700 dark:text-blue-300">{d.mrr_location_eur != null ? `${d.mrr_location_eur.toFixed(2)} €` : '–'}</p>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-muted-foreground">Mitarbeiter-MRR</p>
              <p className="text-sm font-bold text-purple-700 dark:text-purple-300">{d.mrr_employee_eur != null ? `${d.mrr_employee_eur.toFixed(2)} €` : '–'}</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800/40 rounded-lg">
              <p className="text-xs text-muted-foreground">Sonstiges</p>
              <p className="text-sm font-bold">{d.mrr_other_eur != null ? `${d.mrr_other_eur.toFixed(2)} €` : '–'}</p>
            </div>
          </div>

          {/* Pro Abonnement */}
          <div className="space-y-2">
            {breakdown.map((sub, i) => (
              <div key={i} className={`p-3 rounded-lg border text-xs ${
                sub.cancel_at_period_end
                  ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-700'
                  : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-gray-500">…{sub.subscription_id_short}</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                      sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>{sub.status}</span>
                    {sub.cancel_at_period_end && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Kündigung zum Laufzeitende</span>}
                    {sub.discount_applied && <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Rabatt</span>}
                  </div>
                  <span className="font-bold text-sm">{sub.total_mrr_display}</span>
                </div>
                {/* Positionen */}
                <div className="space-y-0.5 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                  {(sub.items || []).map((item, j) => (
                    <div key={j} className="flex items-center justify-between text-muted-foreground">
                      <span>{item.category} · {item.billing_scheme === 'tiered' ? `Tiered (${item.tiers_mode})` : 'Festpreis'} · Qty {item.quantity}</span>
                      <span className="font-medium text-foreground">{item.monthly_display}/Mo</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            MRR = monatlich wiederkehrender Nettoumsatz · exkl. Steuer · exkl. Einmalpositionen · exkl. Testphasen
          </p>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, sub, icon: Icon, color = 'blue', warn = false, loading }) {
  const colorMap = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600',
    green:  'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600',
    amber:  'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600',
    red:    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-600',
    gray:   'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-500',
  };
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${colorMap[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{title}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      {loading ? (
        <div className="h-7 flex items-center"><Loader2 className="h-4 w-4 animate-spin opacity-50" /></div>
      ) : (
        <p className="text-2xl font-bold">{value ?? '–'}</p>
      )}
      {sub && <p className="text-xs opacity-70">{sub}</p>}
    </div>
  );
}

export default function StripeOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/superadmin/stripe-cc/overview`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden der Übersicht');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const d = data || {};

  return (
    <div className="space-y-6">
      {/* Fehler */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
          <XCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Verbindungsstatus */}
      {!loading && data && (
        <div className={`flex items-center justify-between p-3 rounded-xl border ${
          d.stripe_connected
            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {d.stripe_connected
              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
              : <XCircle className="h-4 w-4 text-red-600" />}
            <span className="text-sm font-medium">
              Stripe {d.stripe_connected ? 'verbunden' : 'nicht verbunden'}
            </span>
            {d.mode_label && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                d.mode === 'live'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {d.mode_label}
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard
          title="Monatlicher Umsatz (MRR)"
          value={d.mrr_display || (d.mrr_eur != null ? `${d.mrr_eur.toFixed(2)} €` : null)}
          sub="aus aktiven Abonnements"
          icon={TrendingUp}
          color="green"
          loading={loading}
        />
        <KpiCard
          title="Aktive Abonnements"
          value={d.active_subscriptions}
          sub={d.trialing_subscriptions ? `+ ${d.trialing_subscriptions} in Testphase` : undefined}
          icon={CheckCircle2}
          color="indigo"
          loading={loading}
        />
        <KpiCard
          title="Offene Rechnungen"
          value={d.open_invoices_display || (d.open_invoices_count != null ? `${d.open_invoices_count} Stk.` : null)}
          sub={d.open_invoices_count ? `${d.open_invoices_count} Rechnungen offen` : undefined}
          icon={FileText}
          color={d.open_invoices_count > 0 ? 'amber' : 'gray'}
          loading={loading}
        />
        <KpiCard
          title="Zahlungen überfällig"
          value={d.past_due_subscriptions}
          sub="Abonnements mit Zahlungsproblem"
          icon={AlertTriangle}
          color={d.past_due_subscriptions > 0 ? 'red' : 'gray'}
          loading={loading}
        />
      </div>

      {/* Zweite Zeile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard
          title="Kunden gesamt"
          value={d.total_customers}
          sub={`davon ${d.active_customers || 0} aktiv`}
          icon={Users}
          color="blue"
          loading={loading}
        />
        <KpiCard
          title="In Testphase"
          value={d.trialing_subscriptions ?? d.trialing_count_db}
          sub="aktive Trials"
          icon={Clock}
          color="amber"
          loading={loading}
        />
        <KpiCard
          title="Fehlgeschlagen"
          value={d.failed_payments_count}
          sub="fehlgeschlagene Zahlungen"
          icon={XCircle}
          color={d.failed_payments_count > 0 ? 'red' : 'gray'}
          loading={loading}
        />
        <KpiCard
          title="Mit Stripe"
          value={d.subs_with_stripe}
          sub="Abonnements mit Stripe-ID"
          icon={CreditCard}
          color="indigo"
          loading={loading}
        />
      </div>

      {/* Warnungen */}
      {!loading && data && (d.past_due_subscriptions > 0 || d.failed_payments_count > 0 || d.open_invoices_count > 0) && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Offene Aufgaben</h3>
          {d.past_due_subscriptions > 0 && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                  {d.past_due_subscriptions} Abonnement{d.past_due_subscriptions !== 1 ? 'e' : ''} mit überfälliger Zahlung
                </p>
                <p className="text-xs text-red-600 dark:text-red-400">Bitte im "Kunden"-Tab prüfen und ggf. Zahlung wiederholen.</p>
              </div>
            </div>
          )}
          {d.open_invoices_count > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl">
              <FileText className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                  {d.open_invoices_count} offene Rechnung{d.open_invoices_count !== 1 ? 'en' : ''} ({d.open_invoices_display})
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">Details im "Rechnungen"-Tab.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MRR-Aufschlüsselung */}
      {!loading && data && d.mrr_breakdown && d.mrr_breakdown.length > 0 && (
        <MrrBreakdown breakdown={d.mrr_breakdown} d={d} />
      )}
    </div>
  );
}
